import React, { useEffect } from "react";
import { useState, useContext } from "react";

import errorBubbleBackground from "../assets/errorBubble.png";
import styles from "./FakeWebsite.module.css";
import StyledButton from "../components/StyledButton";
import AmountFound from "../features/AmountFound";
import { green } from "../config";
import useShowingCorrectAnswer from "../hooks/useShowingCorrectAnswer";
import { SelectAnswerContext } from "../contexts/selectAnswerContext";
import HTMLRenderer from "@natklok/components/HTMLRenderer";
import { FakeWebsiteRenderProps } from "@common";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

interface RenderProps extends FakeWebsiteRenderProps {
  readOnly?: boolean;
  showCorrect?: boolean;
}

const FakeWebsite: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({ readOnly = false, showCorrect = false, websiteHTML = "" }) => {
  const { onSelectAnswer } = useContext(SelectAnswerContext);
  const [found, setFound] = useState<Record<number, boolean>>({ 1: false, 2: false, 3: false });
  const translatePageText = useTranslatePageText();

  const allElements = document.querySelectorAll("*"); // Select all elements
  const suspiciousElements = Array.from(allElements).filter((el) => el.id.startsWith("suspicious")); // Filter out the ones with ids starting with "suspicious"

  const showingCorrectAnswer = useShowingCorrectAnswer();
  const displayCorrectAnswers = showCorrect || showingCorrectAnswer;

  useEffect(() => {
    if (
      Object.values(found).filter((v) => v === true).length === suspiciousElements.length &&
      suspiciousElements.length !== 0
    ) {
      removeErrorBubbles();
      const answerStats = { val1: 0, val2: 1, val3: 0 };
      onSelectAnswer("correct", answerStats);
    }
  }, [found, onSelectAnswer, suspiciousElements]);

  useEffect(() => {
    if (displayCorrectAnswers === true) {
      for (let i = 0; i < suspiciousElements.length; i++) {
        setOutLine(suspiciousElements[i]);
      }
    } else {
      for (let i = 0; i < suspiciousElements.length; i++) {
        eraseOutline(suspiciousElements[i]);
      }
    }
  }, []);

  useEffect(() => {
    if (!readOnly) {
      const clickListener = (event: MouseEvent) => {
        const { target } = event;

        // If the clicked target or its ancestors contain a suspicious id, handle it
        const clickedSuspiciousElement1 = (target as HTMLElement).closest("#suspicious1");
        const clickedSuspiciousElement2 = (target as HTMLElement).closest("#suspicious2");
        const clickedSuspiciousElement3 = (target as HTMLElement).closest("#suspicious3");
        if (clickedSuspiciousElement1) {
          setFound({ ...found, 1: true });

          setOutLine(suspiciousElements[0]);
        } else if (clickedSuspiciousElement2) {
          setFound({ ...found, 2: true });
          setOutLine(clickedSuspiciousElement2);
          setOutLine(suspiciousElements[1]);
        } else if (clickedSuspiciousElement3) {
          setFound({ ...found, 3: true });
          setOutLine(clickedSuspiciousElement3);
          setOutLine(suspiciousElements[2]);
        } else if (target !== null) {
          const x = event.clientX;
          const y = event.clientY;
          generateErrorBubble(x, y);
        }
      };
      document.addEventListener("click", clickListener);
      return () => {
        document.removeEventListener("click", clickListener);
      };
    }
  }, [readOnly, found, suspiciousElements]);

  const generateErrorBubble = (x: number, y: number) => {
    const errorBubble = document.createElement("div");
    errorBubble.classList.add(styles.errorBubble);
    errorBubble.style.backgroundImage = `url(${errorBubbleBackground})`;
    errorBubble.style.left = `${x - 30}px`;
    errorBubble.style.top = `${y - 30}px`;
    document.body.appendChild(errorBubble);
  };

  const removeErrorBubbles = () => {
    const bubbles = document.getElementsByClassName(styles.errorBubble);
    while (bubbles.length > 0) {
      bubbles[0].parentNode?.removeChild(bubbles[0]);
    }
  };

  const setOutLine = (el: any) => {
    el.style.outline = `3px solid ${green[500]}`;
    el.style.background = "rgba(62,189,147,0.3)";
  };

  const eraseOutline = (el: any) => {
    el.style.outline = "0px solid";
    el.style.background = "";
  };

  return (
    <>
      <AmountFound
        scams={suspiciousElements}
        amountFound={Object.values(found).filter(Boolean).length}
      />
      <HTMLRenderer>{websiteHTML}</HTMLRenderer>
      <StyledButton
        hasCooldown
        variant="secondary"
        onClick={() => {
          removeErrorBubbles();
          const answerStats = { val1: 0, val2: 0, val3: 1 };
          onSelectAnswer("incorrect", answerStats);
        }}
        style={{ marginTop: 10 }}
        label={translatePageText("general.dontKnowContinue")}
        disabled={readOnly}
      />
    </>
  );
};

export default FakeWebsite;
